import { create } from 'zustand';
import type { Rack } from '../models/types';

export type EditSubMode = 'add' | 'delete' | 'wall';

const DEFAULT_SHELVES_PER_RACK = 5;
const DEFAULT_SLOTS_PER_SHELF = 5;

export interface WarehouseMapState {
  //  Grid state
  localRows: number | null;
  localCols: number | null;
  displayRows: string | null;
  displayCols: string | null;

  //  Rack operations — single sub-mode instead of separate booleans
  editMode: boolean;
  currentSubMode: EditSubMode;

  selectedCells: Set<string>;
  deletedRackIds: Set<number>;
  localRacks: Rack[];
  localWallCells: Set<string>;

  //  UI state
  showCreateForm: boolean;
  showLocationModal: boolean;
  selectedRack: Rack | null;

  //  Pending form values
  preFillRow: number | null;
  preFillCol: number | null;
  pendingShelves: number;
  pendingSlotsPerShelf: number;
  pendingIsEndcap: boolean;

  //  Actions
  setEditMode: (v: boolean) => void;
  setCurrentSubMode: (mode: EditSubMode) => void;
  setShowCreateForm: (v: boolean) => void;
  setShowLocationModal: (v: boolean) => void;
  setSelectedRack: (r: Rack | null) => void;
  setDisplayRows: (v: string | null) => void;
  setDisplayCols: (v: string | null) => void;
  setLocalRows: (v: number | null) => void;
  setLocalCols: (v: number | null) => void;
  setSelectedCells: (fn: React.SetStateAction<Set<string>>) => void;
  setDeletedRackIds: (fn: React.SetStateAction<Set<number>>) => void;
  setLocalRacks: (fn: React.SetStateAction<Rack[]>) => void;
  resetAll: () => void;
  resetLocalState: () => void;

  //  Form value setters
  setPendingShelves: (v: number) => void;
  setPendingSlotsPerShelf: (v: number) => void;
  setPendingIsEndcap: (v: boolean) => void;

  //  Wall helpers
  toggleWallCell: (x: number, y: number) => void;
  clearWalls: () => void;

  //  Form helpers
  setPreFillRow: (v: number | null) => void;
  setPreFillCol: (v: number | null) => void;
}

export const useWarehouseMapStore = create<WarehouseMapState>((set, get) => ({
  //  Initial state
  localRows: null,
  localCols: null,
  displayRows: null,
  displayCols: null,

  editMode: false,
  currentSubMode: 'add',
  selectedCells: new Set<string>(),
  deletedRackIds: new Set<number>(),
  localRacks: [],
  localWallCells: new Set<string>(),

  showCreateForm: false,
  showLocationModal: false,
  selectedRack: null,

  preFillRow: 0,
  preFillCol: 0,
  pendingShelves: DEFAULT_SHELVES_PER_RACK,
  pendingSlotsPerShelf: DEFAULT_SLOTS_PER_SHELF,
  pendingIsEndcap: false,

  //  Actions
  setEditMode: (v) => set({ editMode: v }),
  setCurrentSubMode: (mode) => set({ currentSubMode: mode }),
  setShowCreateForm: (v) => set({ showCreateForm: v }),
  setShowLocationModal: (v) => set({ showLocationModal: v }),
  setSelectedRack: (r) => set({ selectedRack: r }),
  setDisplayRows: (v) => set({ displayRows: v }),
  setDisplayCols: (v) => set({ displayCols: v }),
  setLocalRows: (v) => set({ localRows: v }),
  setLocalCols: (v) => set({ localCols: v }),

  setSelectedCells: (fn) => {
    const current = get().selectedCells;
    set({ selectedCells: typeof fn === 'function' ? fn(current) : fn });
  },

  setDeletedRackIds: (fn) => {
    const current = get().deletedRackIds;
    set({ deletedRackIds: typeof fn === 'function' ? fn(current) : fn });
  },

  setLocalRacks: (fn) => {
    const current = get().localRacks;
    set({ localRacks: typeof fn === 'function' ? fn(current) : fn });
  },

  //  Reset helpers
  resetAll: () => {
    set({
      selectedCells: new Set(),
      deletedRackIds: new Set(),
      localRows: null,
      localCols: null,
      displayRows: null,
      displayCols: null,
      localRacks: [],
      localWallCells: new Set(),
      preFillRow: 0,
      preFillCol: 0,
      selectedRack: null,
      showCreateForm: false,
      showLocationModal: false,
      pendingShelves: DEFAULT_SHELVES_PER_RACK,
      pendingSlotsPerShelf: DEFAULT_SLOTS_PER_SHELF,
      pendingIsEndcap: false,
      currentSubMode: 'add',
    });
  },

  resetLocalState: () => {
    set({
      localRacks: [],
      deletedRackIds: new Set(),
      localRows: null,
      localCols: null,
    });
  },

  //  Wall helpers
  toggleWallCell: (x, y) => {
    const key = `${y}:${x}`;
    const current = get().localWallCells;
    const has = current.has(key);

    if (has) {
      set({ localWallCells: new Set(Array.from(current).filter((k) => k !== key)) });
    } else {
      set({ localWallCells: new Set(Array.from(current).concat(key)) });
    }
  },

  clearWalls: () => {
    set({ localWallCells: new Set() });
  },

  //  Form helpers
  setPreFillRow: (v) => set({ preFillRow: v }),
  setPreFillCol: (v) => set({ preFillCol: v }),

  //  Form value setters
  setPendingShelves: (v) => set({ pendingShelves: v }),
  setPendingSlotsPerShelf: (v) => set({ pendingSlotsPerShelf: v }),
  setPendingIsEndcap: (v) => set({ pendingIsEndcap: v }),
}));
